import { ref, watch, type Ref } from 'vue'

/**
 * Persist filters in localStorage.
 * - Läser initialt från localStorage (om finns), annars från initial-parametern (om given).
 * - Sparar varje förändring automatiskt.
 * - Håller sig till de keys som anges i filterKeys.
 */
export function useFilterStorage<
  TKeys extends readonly string[]
>(
  storageKey: string,
  filterKeys: TKeys,
  initial?: Partial<Record<TKeys[number], string[]>>
): {
  filters: Ref<Record<TKeys[number], string[]>>
  reset: () => void
} {
  const makeEmpty = () => {
    const obj: Record<string, string[]> = {}
    for (const k of filterKeys) obj[k] = []
    return obj as Record<TKeys[number], string[]>
  }

  const filters = ref<Record<TKeys[number], string[]>>(makeEmpty())

  const load = () => {
    if (typeof window === 'undefined') return
    try {
      const raw = localStorage.getItem(storageKey)
      if (!raw) {
        // seed med ev. initialvärden
        if (initial) {
          filters.value = { ...filters.value, ...initial } as Record<TKeys[number], string[]>
        }
        return
      }
      const parsed = JSON.parse(raw)
      const next = makeEmpty()
      for (const k of filterKeys) {
        const arr = Array.isArray(parsed?.[k]) ? parsed[k].map(String) : []
        next[k] = arr
      }
      filters.value = next
    } catch {
      // Ignorera korrupt storage
    }
  }

  const save = () => {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters.value))
    } catch {
      // Safari privat läge etc – ignorera
    }
  }

  load()
  watch(filters, save, { deep: true })

  const reset = () => {
    filters.value = makeEmpty()
    save()
  }

  return { filters, reset }
}